import React, { useEffect, useState } from 'react';
import {
  Table, Select, Switch, Tag, Tooltip, message, Button, Modal, Form, Input, Space, Popconfirm, DatePicker,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getLabelProcessingRules, createLabelProcessingRule, updateLabelProcessingRule, deleteLabelProcessingRule,
  getGateways, getLogisticsProviders,
} from '../../../services/logistics';
import type { LabelProcessingRule, Gateway, LogisticsProvider } from '../../../services/logistics';

const REGION_CONFIG_PLACEHOLDER = `{
  "cover_boxes": [
    {"x0": 0.39, "y0": 0.92, "x1": 0.61, "y1": 0.97}
  ],
  "protected_zones": [
    {"x0": 0.87, "y0": 0.91, "x1": 0.97, "y1": 0.97, "label": "qr_code"}
  ]
}`;

const LabelProcessingRuleTab: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<LabelProcessingRule[]>([]);
  const [gatewayOptions, setGatewayOptions] = useState<Gateway[]>([]);
  const [providerOptions, setProviderOptions] = useState<LogisticsProvider[]>([]);
  const [togglingIds, setTogglingIds] = useState<Set<number>>(new Set());

  const [editing, setEditing] = useState<Partial<LabelProcessingRule> | null>(null);
  const [regionConfigText, setRegionConfigText] = useState('');
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const processingType = Form.useWatch('processing_type', form);

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await getLabelProcessingRules({ page_size: 200 });
      const body = resp.data as any;
      setData(Array.isArray(body?.results) ? body.results : (Array.isArray(body) ? body : []));
    } catch {
      message.error('获取标签处理规则失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchOptions = async () => {
    try {
      const [gwResp, providerResp] = await Promise.all([
        getGateways({ page_size: 200 }),
        getLogisticsProviders({ page_size: 200 }),
      ]);
      const gwBody = gwResp.data as any;
      const providerBody = providerResp.data as any;
      setGatewayOptions(Array.isArray(gwBody?.results) ? gwBody.results : (Array.isArray(gwBody) ? gwBody : []));
      setProviderOptions(Array.isArray(providerBody?.results) ? providerBody.results : (Array.isArray(providerBody) ? providerBody : []));
    } catch {
      message.error('获取网关/承运商选项失败');
    }
  };

  useEffect(() => {
    fetchData();
    fetchOptions();
  }, []);

  const patchLocalRow = (id: number, updated: Partial<LabelProcessingRule>) => {
    setData((prev) => prev.map((row) => (row.id === id ? { ...row, ...updated } : row)));
  };

  const handleToggleActive = async (record: LabelProcessingRule, checked: boolean) => {
    setTogglingIds((prev) => new Set(prev).add(record.id));
    try {
      const resp = await updateLabelProcessingRule(record.id, { is_active: checked });
      patchLocalRow(record.id, resp.data);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '更新失败');
    } finally {
      setTogglingIds((prev) => { const next = new Set(prev); next.delete(record.id); return next; });
    }
  };

  const openCreate = () => {
    setEditing({});
    form.resetFields();
    form.setFieldsValue({ processing_type: 'none', is_active: true });
    setRegionConfigText('');
  };

  const openEdit = (record: LabelProcessingRule) => {
    setEditing(record);
    form.setFieldsValue({
      gateway: record.gateway,
      logistics_provider: record.logistics_provider ?? undefined,
      processing_type: record.processing_type,
      is_active: record.is_active,
      verified_at: record.verified_at ? dayjs(record.verified_at) : undefined,
      remark: record.remark,
    });
    setRegionConfigText(record.region_config && Object.keys(record.region_config).length
      ? JSON.stringify(record.region_config, null, 2)
      : '');
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteLabelProcessingRule(id);
      message.success('已删除');
      fetchData();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '删除失败');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const isCreate = !editing?.id;

      let regionConfig: Record<string, unknown> = {};
      const trimmed = regionConfigText.trim();
      if (trimmed) {
        try {
          regionConfig = JSON.parse(trimmed);
        } catch {
          message.error('区域配置不是合法的 JSON 格式');
          return;
        }
        if (typeof regionConfig !== 'object' || Array.isArray(regionConfig) || regionConfig === null) {
          message.error('区域配置必须是一个 JSON 对象');
          return;
        }
      } else if (values.processing_type === 'cover_region') {
        message.error('处理类型是"遮盖指定区域"时，必须填写区域配置');
        return;
      }

      setSaving(true);
      const payload = {
        gateway: values.gateway,
        logistics_provider: values.logistics_provider ?? null,
        processing_type: values.processing_type,
        region_config: regionConfig,
        is_active: values.is_active,
        verified_at: values.verified_at ? values.verified_at.toISOString() : null,
        remark: values.remark || '',
      };
      if (isCreate) {
        await createLabelProcessingRule(payload);
      } else {
        await updateLabelProcessingRule(editing!.id!, payload);
      }
      message.success('保存成功');
      setEditing(null);
      fetchData();
    } catch (e: any) {
      if (e?.errorFields) return;
      const detail = e?.response?.data;
      const firstError = detail && typeof detail === 'object' ? Object.values(detail)[0] : null;
      message.error((Array.isArray(firstError) ? firstError[0] : firstError) || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<LabelProcessingRule> = [
    { title: '网关', dataIndex: 'gateway_name', width: 160, render: (v: string, r) => `${v}（${r.gateway_code}）` },
    {
      title: '承运商',
      dataIndex: 'logistics_provider_name',
      width: 140,
      render: (v?: string | null) => (v ? v : <Tag>全部承运商（兜底规则）</Tag>),
    },
    { title: '处理类型', dataIndex: 'processing_type_display', width: 140 },
    {
      title: '启用',
      key: 'is_active',
      width: 80,
      render: (_: any, r: LabelProcessingRule) => (
        <Switch checked={r.is_active} disabled={togglingIds.has(r.id)} onChange={(c) => handleToggleActive(r, c)} />
      ),
    },
    {
      title: '最近核实时间',
      dataIndex: 'verified_at',
      width: 160,
      render: (v?: string | null) => (v ? new Date(v).toLocaleString('zh-CN') : <Tag color="orange">未核实</Tag>),
    },
    {
      title: '备注',
      dataIndex: 'remark',
      width: 240,
      ellipsis: true,
      render: (text?: string) => (text ? <Tooltip title={text}><span>{text}</span></Tooltip> : '-'),
    },
    {
      title: '操作',
      key: 'action',
      width: 130,
      fixed: 'right',
      render: (_: any, r: LabelProcessingRule) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>编辑</Button>
          <Popconfirm title="确定删除这条规则？" onConfirm={() => handleDelete(r.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 12, color: '#999' }}>
        网关返回的运单标签，展示给客户之前要不要处理（比如遮盖标签上的网关自身 logo）、怎么处理，
        按"网关 + 承运商"配置在这里，不用改代码——承运商留空表示这个网关下所有承运商都适用（兜底规则）。
        没有命中任何规则的组合，默认原样展示原始标签，不会自作主张处理。区域坐标需要拿真实标签样本量出来，
        不要凭感觉估计；改完规则立刻对新的查看/下载请求生效，不影响已经生成的历史标签文件。
      </div>
      <div style={{ marginBottom: 12 }}>
        <Button type="dashed" icon={<PlusOutlined />} onClick={openCreate}>新增规则</Button>
      </div>
      <Table<LabelProcessingRule>
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={false}
        scroll={{ x: 1100 }}
      />

      <Modal
        title={editing?.id ? '编辑标签处理规则' : '新增标签处理规则'}
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={handleSave}
        confirmLoading={saving}
        destroyOnClose
        width={640}
      >
        <Form form={form} layout="vertical">
          <Space size="middle" style={{ width: '100%' }}>
            <Form.Item name="gateway" label="网关" rules={[{ required: true, message: '请选择网关' }]} style={{ width: 260 }}>
              <Select
                showSearch
                optionFilterProp="label"
                options={gatewayOptions.map((g) => ({ value: g.id, label: `${g.name}（${g.code}）` }))}
                placeholder="选择网关"
              />
            </Form.Item>
            <Form.Item
              name="logistics_provider"
              label="承运商（留空=全部承运商，兜底规则）"
              style={{ width: 320 }}
              tooltip="留空表示这个网关下所有承运商都适用这条规则；某个承运商有专属规则时，专属规则优先于兜底规则"
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={providerOptions.map((p) => ({ value: p.id, label: p.name }))}
                placeholder="留空表示全部承运商"
              />
            </Form.Item>
          </Space>
          <Form.Item name="processing_type" label="处理类型" rules={[{ required: true, message: '请选择处理类型' }]}>
            <Select
              options={[
                { value: 'none', label: '不处理（原样展示原始标签）' },
                { value: 'cover_region', label: '遮盖指定区域（比如网关自身的 logo）' },
              ]}
            />
          </Form.Item>
          {processingType === 'cover_region' && (
            <Form.Item
              label="区域配置（JSON，坐标是页面宽高的比例，原点在左上角）"
              required
              tooltip="cover_boxes 是要遮盖的区域，protected_zones 是绝对不能被遮盖框碰到的保护区（比如二维码）；
                两者有几何重叠时会拒绝处理、原样返回原始标签，不会盖错地方。坐标要拿真实标签样本量出来，不要凭感觉估计。"
            >
              <Input.TextArea
                rows={8}
                value={regionConfigText}
                onChange={(e) => setRegionConfigText(e.target.value)}
                placeholder={REGION_CONFIG_PLACEHOLDER}
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Item>
          )}
          <Space size="middle" style={{ width: '100%' }}>
            <Form.Item name="is_active" label="启用" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item
              name="verified_at"
              label="最近核实时间"
              tooltip="最后一次拿真实标签样本核对过这条规则坐标的时间，帮助判断规则是否可能已经过期（比如网关调整了自己的标签模板）"
              style={{ width: 280 }}
            >
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} placeholder="比如坐标是从哪几张真实样本量出来的" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LabelProcessingRuleTab;
